/**
 * `/policy/anti-cheat` — public-facing anti-cheat policy page.
 *
 * Deliberately no numbers, weights, thresholds, or factor names. The
 * point is to reassure honest players (and warn opportunists) that
 * we detect and penalise cheating without giving a bot operator a
 * checklist to tune against. Written as plain English T&C, not as
 * marketing copy.
 *
 * Reached from:
 *   - the anti-cheat home banner (`/` when the admin flag is on)
 *   - the "Anti-cheat policy — Know more" link on the live-trivia
 *     detail page
 *   - direct link (share-able URL)
 */

export async function renderAntiCheatPolicy(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Anti-cheat</div>
    </div>
    <div class="pad">
      <div class="hero violet center" style="padding:24px 18px;">
        <div style="font-size:36px;line-height:1;">🛡️</div>
        <div style="font-family:var(--font-display);font-size:13px;letter-spacing:5px;font-weight:700;opacity:0.9;margin-top:10px;">
          FAIR PLAY
        </div>
        <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:700;margin-top:8px;line-height:1.25;">
          We reward excellence,<br/>not cheats.
        </div>
        <div style="font-size:12.5px;font-weight:500;opacity:0.92;margin-top:8px;line-height:1.5;">
          Every prize on Triviana comes out of a pool that real players
          paid into. Protecting it is not optional.
        </div>
      </div>

      <div class="card" style="padding:16px;margin-top:12px;">
        <div style="font-family:var(--font-display);font-size:14px;color:#fff;font-weight:700;margin-bottom:6px;">
          Our promise to honest players
        </div>
        <div class="muted" style="font-size:13px;line-height:1.55;">
          Winning on Triviana takes knowledge, speed, and preparation. When
          someone tries to shortcut that with automation, coordinated
          accounts, or third-party assistance, they are taking money out
          of an honest player's pocket. We do not treat that lightly.
        </div>
      </div>

      <h3 class="sec">What we watch for</h3>
      <div class="card" style="padding:14px 16px;">
        <ul style="margin:0;padding-left:18px;color:var(--ink-2);font-size:13px;line-height:1.7;">
          <li>Answers submitted at speeds no human reader could sustain.</li>
          <li>Answer patterns that do not react to how hard a question is.</li>
          <li>Fresh accounts winning prize positions on the first attempt at
              questions we designed to be hard.</li>
          <li>Groups of accounts acting as one — same device, same network,
              same behaviour.</li>
          <li>Any use of external tools, plugins, screen readers, or
              coordinated messaging to answer live questions.</li>
        </ul>
      </div>

      <h3 class="sec">What happens if a game is flagged</h3>
      <div class="card" style="padding:14px 16px;">
        <div class="muted" style="font-size:13px;line-height:1.6;">
          At the end of every live trivia, before prizes are settled, we
          review the play of every top-ranked participant. Where the play
          is consistent with cheating, that participant's score is reduced
          — sometimes to zero — and the prize flows to the next honest
          player in the ranking. This happens automatically and takes
          only seconds.
        </div>
      </div>

      <h3 class="sec">Repeat offenders</h3>
      <div class="card" style="padding:14px 16px;">
        <div class="muted" style="font-size:13px;line-height:1.6;">
          A single flagged game reduces one prize. A pattern across multiple
          games leads to permanent action on the account, including
          suspension and forfeiture of any wallet balance that was won
          through unfair play. Farming, multi-accounting, and selling
          Triviana accounts are grounds for permanent removal.
        </div>
      </div>

      <h3 class="sec">If you think we got it wrong</h3>
      <div class="card" style="padding:14px 16px;">
        <div class="muted" style="font-size:13px;line-height:1.6;">
          We would rather over-protect the prize pool than let a real
          cheat through, so some honest players will occasionally be
          affected. If you believe your result was reduced in error,
          contact support with the trivia name and the time you played.
          A human on our team will look at your case — we keep the full
          detail of every review for exactly this reason.
        </div>
        <div style="margin-top:12px;">
          <a href="mailto:biliyokaaron@gmail.com?subject=Anti-cheat%20review%20request"
             class="btn ghost" style="margin:0;font-size:13px;">
            Contact support
          </a>
        </div>
      </div>

      <div class="muted center" style="font-size:11px;margin-top:18px;line-height:1.5;">
        This policy is part of the Triviana Terms of Service and can be
        updated at any time. Continuing to play indicates acceptance.
      </div>

      <div style="margin-top:18px;">
        <a href="/live" data-link class="btn gold" style="display:block;text-align:center;">
          Back to live trivia
        </a>
      </div>
    </div>
  `;
}
